// client/src/components/UpdateNoticePopup.js
// v2 전환 기간 업데이트 예고 팝업. 10/10 v2 배포 시 제거 (config/maintenance.js 참조)
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  V2_NOTICE_POPUP_ENABLED, V2_UPDATE_DATE_TEXT, V2_UPDATE_AT, V2_UPDATE_DAY_START,
  V2_NOTICE_HIDE_KEY, V2_NOTICE_HIDE_DAYS,
} from '../config/maintenance';
import '../styles/components/UpdateNoticePopup.css';

/** 가입 퍼널 보호: 회원가입 경로에서는 노출 보류 (이동 후 재평가) */
const EXCLUDED_PATHS = ['/register', '/influencer/register', '/marketer/register'];
const SESSION_KEY = 'v2NoticeSeenThisSession';
const SHOW_DELAY_MS = 400; // 랜딩 → 역할별 마이비즈 리다이렉트 완료 후 경로로 판정

function shouldShow() {
  if (!V2_NOTICE_POPUP_ENABLED) return false;
  if (Date.now() >= V2_UPDATE_AT) return false; // 업데이트일 도래 시 신규 노출 중단
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return false; // 세션당 1회
    const raw = localStorage.getItem(V2_NOTICE_HIDE_KEY);
    const hideUntil = Number(raw);
    if (raw !== null && !Number.isFinite(hideUntil)) {
      localStorage.removeItem(V2_NOTICE_HIDE_KEY); // 손상 값 복구
      return true;
    }
    return Date.now() >= (hideUntil || 0);
  } catch {
    return true; // 스토리지 불가 환경(사파리 프라이빗 등)에서는 그냥 노출
  }
}

function getAudience(pathname) {
  if (pathname.startsWith('/marketer')) return 'marketer';
  if (pathname.startsWith('/influencer')) return 'influencer';
  return 'guest';
}

const COPY = {
  marketer: {
    title: `${V2_UPDATE_DATE_TEXT}, 셀프애드가 완전히 새로워집니다`,
    body: (
      <>
        더 나은 서비스를 위한 대규모 업데이트를 준비하고 있어요.<br />
        업데이트 기간 동안 <b>신규 프로젝트 생성이 일시 중단</b>됩니다.<br />
        진행 중인 프로젝트, 보유 포인트, 제출된 결과물과 결산 일정은<br />
        업데이트 이후에도 <b>그대로 유지</b>되니 안심하세요.
      </>
    ),
  },
  influencer: {
    title: `${V2_UPDATE_DATE_TEXT}, 셀프애드가 완전히 새로워집니다`,
    body: (
      <>
        더 나은 서비스를 위한 대규모 업데이트를 준비하고 있어요.<br />
        새로운 프로젝트 공고는 <b>업데이트 이후 순차적으로</b> 열립니다.<br />
        진행 중인 프로젝트의 결과물 제출·정산과 보유 포인트는<br />
        <b>그대로 진행·유지</b>되니 안심하세요.
      </>
    ),
  },
  guest: {
    title: `${V2_UPDATE_DATE_TEXT}, 셀프애드가 새로워집니다`,
    body: (
      <>
        데이터 기반 인플루언서 매칭과 입찰 시스템을 담은<br />
        대규모 업데이트를 준비하고 있어요. 기대해 주세요!
      </>
    ),
  },
};

export default function UpdateNoticePopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState('guest'); // 노출 시점에 고정 — 이후 경로 변경으로 문구가 바뀌지 않음
  const [hideFor7Days, setHideFor7Days] = useState(false);
  const shownRef = useRef(false);

  // 경로 변경마다 재평가: 가입 페이지로 시작한 세션도 가입 완료 후 노출됨
  useEffect(() => {
    if (shownRef.current) return;
    const normalized = pathname.replace(/\/+$/, '') || '/';
    if (EXCLUDED_PATHS.includes(normalized)) return;
    const t = setTimeout(() => {
      if (shownRef.current || !shouldShow()) return;
      shownRef.current = true;
      setAudience(getAudience(window.location.pathname)); // 지연 후의 실제 경로 기준
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {} // 노출 확정 시점에 기록(리마운트 재노출 방지)
      setOpen(true);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [pathname]);

  // 스크롤 잠금 + 스크롤바 폭 보정 (LoginModal.js 기존 로직 재사용)
  useEffect(() => {
    if (!open) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add('modal-open');
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.paddingRight = '';
    };
  }, [open]);

  /** 확인 버튼: 체크 상태 반영 */
  const confirm = useCallback(() => {
    if (hideFor7Days) {
      try {
        localStorage.setItem(V2_NOTICE_HIDE_KEY, String(Date.now() + V2_NOTICE_HIDE_DAYS * 24 * 60 * 60 * 1000));
      } catch {}
    }
    setOpen(false);
  }, [hideFor7Days]);

  /** 오버레이·X·ESC: 체크 무시하고 단순 닫기 (7일 숨김은 명시적 확인에서만) */
  const dismiss = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  if (!open) return null;

  // D-day는 달력일 기준(10/10 자정), 자동 숨김 기준(03:00)과 분리 — 00~03시 구간 +1일 오차 방지
  const dday = Math.max(0, Math.ceil((V2_UPDATE_DAY_START - Date.now()) / 86400000));
  const copy = COPY[audience];

  return (
    <div className="v2notice" role="dialog" aria-modal="true" aria-label="업데이트 안내">
      <div className="v2notice__overlay" onClick={dismiss} />
      <div className="v2notice__card">
        <button type="button" className="v2notice__x" onClick={dismiss} aria-label="닫기">✕</button>
        <div className="v2notice__badge">UPDATE&nbsp;D-{dday}</div>
        <h3 className="v2notice__title">{copy.title}</h3>
        <p className="v2notice__desc">{copy.body}</p>
        <label className="v2notice__check">
          <input
            type="checkbox"
            checked={hideFor7Days}
            onChange={(e) => setHideFor7Days(e.target.checked)}
          />
          7일간 다시 보지 않기
        </label>
        <button type="button" className="v2notice__btn" onClick={confirm} autoFocus>확인</button>
      </div>
    </div>
  );
}
